import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { DndContext, closestCenter, DragEndEvent, DragStartEvent, DragOverlay } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Target, CirclePlus, FolderOpen } from 'lucide-react';
import GlassPill from '@/components/ui/GlassPill';
import ContainerButton from '@/components/ui/ContainerButton';
import { Goal } from './GoalCard';
import GoalCard from './GoalCard';
import GoalGhost from './GoalGhost';
import { useDragContext } from '@/contexts/drag/DragContext';
import { motion, AnimatePresence } from 'framer-motion';

interface ActiveGoalsContainerProps {
  goals: Goal[];
  onGoalClick: (goalId: string) => void;
  onReorder: (goals: Goal[]) => void;
  onCreateGoal?: () => void;
}

// Sortable Goal Item Component
const SortableGoalItem: React.FC<{
  goal: Goal;
  onGoalClick: (goalId: string) => void;
}> = ({ goal, onGoalClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: goal.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      className=""
      layoutId={`active-goal-${goal.id}`}
      whileHover={{ scale: 1.02 }}
      transition={{ layout: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1.0] } }}
    >
      <GoalCard 
        goal={goal} 
        onClick={onGoalClick}
        dragHandleRef={setActivatorNodeRef}
        dragListeners={listeners}
        dragAttributes={attributes}
      />
    </motion.div>
  );
};

const ActiveGoalsContainer: React.FC<ActiveGoalsContainerProps> = ({
  goals,
  onGoalClick,
  onReorder,
  onCreateGoal
}) => {
  const navigate = useNavigate();
  const [activeGoal, setActiveGoal] = useState<Goal | null>(null);
  const { setIsDragging } = useDragContext();

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setIsDragging(true);
    const { active } = event;
    const goal = goals.find(g => g.id === active.id);
    if (goal) {
      setActiveGoal(goal);
    }
  }, [goals, setIsDragging]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setIsDragging(false);
    setActiveGoal(null);

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = goals.findIndex(goal => goal.id === active.id);
    const newIndex = goals.findIndex(goal => goal.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const reorderedGoals = arrayMove(goals, oldIndex, newIndex);
      onReorder(reorderedGoals);
    }
  }, [goals, onReorder, setIsDragging]);

  return (
    <div className="relative p-3 md:p-6 rounded-lg overflow-hidden">
      {/* Gradient background - matching card border radius */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/30 to-white/10 dark:from-black/40 dark:to-black/20 -z-10 rounded-lg" />
      
      {/* Top border highlight - matching card border radius */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-300/30 to-transparent dark:via-amber-500/20 rounded-t-lg" />
      
      {/* Glass effect - matching card border radius */}
      <div className="absolute inset-0 backdrop-blur-xl backdrop-saturate-150 -z-10 rounded-lg" />

      {/* Section Header */}
      <div className="mb-3 md:mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Target className="text-purple-500 dark:text-purple-400" size={18} />
            <h3 className="font-medium text-base">Projects</h3>
            {goals.length > 0 && (
              <GlassPill 
                type={goals.length.toString()} 
                variant="purple" 
                className="ml-2 text-xs h-6 w-6 flex items-center justify-center p-0"
              />
            )}
          </div>
          
          {/* Action Buttons - Desktop only */}
          <div className="hidden sm:flex items-center gap-2">
            {/* New Project Button */}
            <ContainerButton
              onClick={onCreateGoal}
              icon={<FolderOpen size={16} />}
            >
              New Project
            </ContainerButton>
          </div>
        </div>
        
        {/* Mobile New Project Button - Full width below header */}
        <div className="sm:hidden mt-3">
          <ContainerButton
            onClick={onCreateGoal}
            icon={<FolderOpen size={16} />}
            className="w-full justify-center"
          >
            New Project
          </ContainerButton>
        </div>
      </div>

      {/* Goals List */}
      {goals.length === 0 ? (
        <EmptyActiveGoalsPlaceholder onCreateProject={onCreateGoal} />
      ) : (
        <DndContext
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={goals.map(g => g.id)} strategy={verticalListSortingStrategy}>
            <AnimatePresence mode="popLayout">
              <div className="space-y-2 md:space-y-4">
                {goals.map((goal) => (
                  <SortableGoalItem
                    key={goal.id}
                    goal={goal}
                    onGoalClick={onGoalClick}
                  />
                ))}
              </div>
            </AnimatePresence>
          </SortableContext>

          {/* Portal DragOverlay to document.body */}
          {typeof window !== 'undefined' && createPortal(
            <DragOverlay
              dropAnimation={null}
              modifiers={[]}
              zIndex={1000}
            >
              {activeGoal ? <GoalGhost goal={activeGoal} /> : null}
            </DragOverlay>,
            document.body
          )}
        </DndContext>
      )}
    </div>
  );
};

// Beautiful placeholder for empty goals state
const EmptyActiveGoalsPlaceholder: React.FC<{ onCreateProject?: () => void }> = ({ onCreateProject }) => {
  const navigate = useNavigate();
  
  const handleCreateProject = () => {
    if (onCreateProject) {
      onCreateProject();
    } else {
      navigate('/project-manager');
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="flex flex-col items-center justify-center px-6 py-10 min-h-[320px]"
    >
      {/* Animated illustration */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        className="relative mb-6"
      >
        {/* Animated background circles */}
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            className="w-20 h-20 rounded-full border border-purple-200/30 dark:border-purple-800/30"
          />
        </div>
        
        {/* Target icon with floating animation */}
        <div className="relative z-10 w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
          <motion.div
            animate={{ y: [0, -2, 0] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
          >
            <Target className="w-8 h-8 text-white" />
          </motion.div>
        </div>
      </motion.div>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.5 }}
        className="text-center mb-6 space-y-3"
      >
        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
          Ready to Achieve More?
        </h3>
        <p className="text-sm text-slate-600 dark:text-slate-400 max-w-xs leading-relaxed">
          Set meaningful goals, track your progress, and turn your aspirations into reality.
        </p>
      </motion.div>

      {/* CTA Button */}
      <motion.button
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.6, duration: 0.5 }}
        whileHover={{ scale: 1.05, y: -1 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleCreateProject}
        className="relative group overflow-hidden bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
      >
        {/* Background animation */}
        <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
        
        {/* Button content */}
        <div className="relative flex items-center gap-2">
          <span>Create Your First Project</span>
          <motion.div
            animate={{ x: [0, 1, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          >
            →
          </motion.div>
        </div>
      </motion.button>

      {/* Subtle helper text */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 0.5 }}
        className="text-xs text-slate-400 dark:text-slate-500 mt-4"
      >
        Start small, think big
      </motion.p>
    </motion.div>
  );
};

export default ActiveGoalsContainer;
